// services/pushService.js
// Web Push subscription management + preference API calls.

import axios from 'axios';

const BASE = process.env.REACT_APP_API_URL || '';
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

// ── iOS / standalone detection ────────────────────────────────────────────────

/** Returns true when running on iOS Safari outside of a standalone PWA install.
 *  Web Push on iOS 16.4+ only works when the app is opened from the Home Screen. */
export function iosInstallNeeded() {
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  const isStandalone =
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true;
  return isIOS && !isStandalone;
}

// ── VAPID key helpers ─────────────────────────────────────────────────────────

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  return Uint8Array.from([...rawData].map(c => c.charCodeAt(0)));
}

// ── Service Worker registration ───────────────────────────────────────────────

export async function registerServiceWorker() {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return null;
  try {
    const reg = await navigator.serviceWorker.register('/sw.js');
    return reg;
  } catch (err) {
    console.warn('[Push] SW registration failed:', err.message);
    return null;
  }
}

// ── Subscribe this device ─────────────────────────────────────────────────────

export async function subscribeDevice() {
  if (iosInstallNeeded()) return 'ios_not_standalone';
  const reg = await registerServiceWorker();
  if (!reg) return false;

  try {
    // Fetch VAPID public key
    const { data } = await axios.get(`${BASE}/api/push/vapid-public-key`);
    if (!data.publicKey) return false;

    const applicationServerKey = urlBase64ToUint8Array(data.publicKey);

    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey,
      });
    }

    const json = sub.toJSON();
    await axios.post(
      `${BASE}/api/push/subscribe`,
      {
        endpoint: json.endpoint,
        keys: json.keys,
        userAgent: navigator.userAgent,
      },
      auth()
    );
    return true;
  } catch (err) {
    console.warn('[Push] subscribe failed:', err.message);
    return false;
  }
}

// ── Unsubscribe this device ───────────────────────────────────────────────────

export async function unsubscribeDevice() {
  if (!('serviceWorker' in navigator)) return;
  try {
    const reg = await navigator.serviceWorker.getRegistration('/sw.js');
    if (!reg) return;
    const sub = await reg.pushManager.getSubscription();
    if (!sub) return;
    const endpoint = sub.toJSON().endpoint;
    await sub.unsubscribe();
    await axios.delete(`${BASE}/api/push/subscribe`, { ...auth(), data: { endpoint } });
  } catch (err) {
    console.warn('[Push] unsubscribe failed:', err.message);
  }
}

// ── Preferences API ───────────────────────────────────────────────────────────

export const getPreferences = () =>
  axios.get(`${BASE}/api/push/preferences`, auth()).then(r => r.data);

export const updatePreferences = (payload) =>
  axios.put(`${BASE}/api/push/preferences`, payload, auth()).then(r => r.data);

export const getDevices = () =>
  axios.get(`${BASE}/api/push/devices`, auth()).then(r => r.data);

export const removeDevice = (id) =>
  axios.delete(`${BASE}/api/push/devices/${id}`, auth()).then(r => r.data);
